import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NewModalComponent from "src/core/components/modal/new-modal.component";
import SkeletonComponent from "src/shared/custom-spinner/skeleton.component";
import GetConfigTable from "src/core/components/table/get-config-table.component";

export type CommandResultsColumn<T> = {
  key: string;
  title: string;
  className?: string;
  isSorted?: boolean;
  render?: (row: T) => React.ReactNode;
};

type CommandResultsModalProps<T> = {
  showModal: boolean;
  handleClose: () => void;
  isLoading: boolean;
  lastSyncDisplay: string;
  data: T[] | null;
  columns: CommandResultsColumn<T>[];
  getRowKey: (row: T) => string | number;
  onConfirm: () => void;
  isRestoreMode?: boolean;
  confirmLabel?: string;
  restoreLabel?: string;
  errorMessage?: string | null;
  emptyMessage?: string;
  successMessage?: string;
  restoreMessage?: string;
};

export function CommandResultsModal<T>({
  showModal,
  handleClose,
  isLoading,
  lastSyncDisplay,
  data,
  columns,
  getRowKey,
  onConfirm,
  isRestoreMode = false,
  confirmLabel = "Backup",
  restoreLabel = "Restore",
  errorMessage,
  emptyMessage = "No data available.",
  successMessage = "The requested configuration parameters have been successfully retrieved from the node.",
  restoreMessage = "Restoring the configuration will revert all current values to the baseline",
}: CommandResultsModalProps<T>) {
  const actionConfig = [
    {
      label: isRestoreMode ? restoreLabel : confirmLabel,
      event: onConfirm,
      variant: "primary" as const,
    },
  ];

  return (
    <NewModalComponent
      showModal={showModal}
      handleClose={handleClose}
      headerTitle={
        <div className="gateway-config__header">
          <span className="history-header__icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </span>
          <div>
            <span className="gateway-config__header-title">
              {isRestoreMode ? `${restoreLabel} Command` : "Command Results"}
            </span>
            <span className="gateway-config__header-subtitle">
              <span className="gateway-config__header-subtitle-icon">
                &#8635;
              </span>
              Last sync: {lastSyncDisplay}
            </span>
          </div>
        </div>
      }
      actionConfig={actionConfig}
    >
      <div className="gateway-config">
        {isLoading && (
          <SkeletonComponent
            wrapperHeight="320px"
            skeletonHeight="44px"
            isLoading={true}
            count={6}
          >
            <span />
          </SkeletonComponent>
        )}

        {!isLoading && errorMessage && (
          <div className="gateway-config__error">{errorMessage}</div>
        )}

        {!isLoading && !errorMessage && data && data.length > 0 && (
          <div>
            <p>{isRestoreMode ? restoreMessage : successMessage}</p>
            <GetConfigTable
              columns={columns as any}
              data={data as any}
              getRowKey={getRowKey as any}
              emptyMessage={emptyMessage}
            />
          </div>
        )}

        {!isLoading && !errorMessage && (!data || data.length === 0) && (
          <div className="gateway-config__empty">{emptyMessage}</div>
        )}
      </div>
    </NewModalComponent>
  );
}
